import React from 'react'
import { useTranslation } from 'react-i18next'
import { Lock, ArrowRight, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatDateInTz } from '../../../utils/timezone'

interface Props {
  occurrenceNumber: number
  scheduledAt: string
  finalizedAt: string
  finalizedBy?: { name?: string | null } | null
  currentNumber: number
  seriesClosed?: boolean
  onBackToCurrent: () => void
  timezone?: string | null
  minutesPdfUrl?: string | null
}

const ReadOnlyOccurrenceBanner: React.FC<Props> = ({
  occurrenceNumber,
  scheduledAt,
  finalizedAt,
  finalizedBy,
  currentNumber,
  seriesClosed,
  onBackToCurrent,
  timezone,
  minutesPdfUrl,
}) => {
  const { t, i18n } = useTranslation()
  const fmtDate = (s: string) => formatDateInTz(s, timezone, 'PP', i18n.language)

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3">
      <Lock className="h-4 w-4 text-amber-600 shrink-0" />
      <div className="flex-1 text-sm text-amber-900">
        <span className="font-medium">
          {t('Meeting_occurrence_readonly_label', {
            number: occurrenceNumber,
            date: fmtDate(scheduledAt),
            defaultValue: `Viewing occurrence #${occurrenceNumber} · ${fmtDate(scheduledAt)}`,
          })}
        </span>
        <span className="text-amber-800/80 ml-2">
          {t('Meeting_occurrence_readonly_finalized', {
            date: fmtDate(finalizedAt),
            by: finalizedBy?.name || '—',
            defaultValue: `Finalized ${fmtDate(finalizedAt)} by ${finalizedBy?.name || '—'}`,
          })}
        </span>
        {seriesClosed && (
          <span className="ml-2 text-amber-800/80">
            {t('Meeting_occurrence_series_closed_hint', {
              defaultValue: '· série je uzavřená',
            })}
          </span>
        )}
      </div>
      {minutesPdfUrl && (
        <Button asChild variant="outline" size="sm" className="h-8">
          <a href={minutesPdfUrl} target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4" />
            {t('Meeting_minutes_download_pdf')}
          </a>
        </Button>
      )}
      {!seriesClosed && (
        <button
          type="button"
          onClick={onBackToCurrent}
          className="text-xs font-medium text-amber-900 hover:text-amber-700 inline-flex items-center gap-1"
        >
          {t('Meeting_occurrence_back_to_current', {
            number: currentNumber,
            defaultValue: `Back to current (#${currentNumber})`,
          })}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

export default ReadOnlyOccurrenceBanner
